// Routes by role with lazy pages. `/login` is public; employee routes live under `/`,
// dispatcher routes under `/dispo`, admin routes under `/admin`.
import type { Role } from "@zeiterfassung/shared";
import { useTranslation } from "react-i18next";
import { createBrowserRouter, Navigate, Outlet, useLocation, useRouteError } from "react-router";
import { useMe } from "@/api/queries/me";
import { DispatcherShell, EmployeeShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { homeFor, routes } from "@/lib/routes";

function FullPageSpinner() {
  const { t } = useTranslation("translation", { keyPrefix: "common" });
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Spinner className="size-8 text-accent" label={t("loading")} />
    </div>
  );
}

// Last line of defence for a render error or a chunk that failed to load (offline, deploy in
// between): a German page with a reload instead of the router's developer message.
function RouteErrorPage() {
  const { t } = useTranslation("translation", { keyPrefix: "common.routeError" });
  const error = useRouteError();
  if (import.meta.env.DEV) console.error(error);
  return (
    <div className="flex min-h-dvh items-center justify-center px-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
        <h1 className="font-semibold text-xl">{t("title")}</h1>
        <p className="text-sm text-zinc-600">{t("hint")}</p>
        <div className="flex flex-col gap-2 self-stretch sm:flex-row sm:justify-center">
          <Button size="lg" onClick={() => window.location.reload()}>
            {t("reload")}
          </Button>
          <Button size="lg" variant="secondary" onClick={() => window.location.assign("/")}>
            {t("home")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Renders the shell for the roles given; sends strangers to /login and other roles home.
function RequireRole({ roles }: { roles: Role[] }) {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <FullPageSpinner />;
  if (!me.data)
    return (
      <Navigate to={`${routes.login}?next=${encodeURIComponent(location.pathname)}`} replace />
    );
  if (!roles.includes(me.data.user.role))
    return <Navigate to={homeFor(me.data.user.role)} replace />;
  return me.data.user.role === "employee" ? (
    <EmployeeShell me={me.data} />
  ) : (
    <DispatcherShell me={me.data} />
  );
}

// Logged-in users never see the login page.
function PublicOnly() {
  const me = useMe();
  if (me.isPending) return <FullPageSpinner />;
  if (me.data) return <Navigate to={homeFor(me.data.user.role)} replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <PublicOnly />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: routes.login,
        lazy: () => import("@/features/auth/LoginPage").then((m) => ({ Component: m.LoginPage })),
      },
      {
        path: routes.loginVerify,
        lazy: () => import("@/features/auth/VerifyPage").then((m) => ({ Component: m.VerifyPage })),
      },
    ],
  },
  {
    element: <RequireRole roles={["employee"]} />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: routes.absences,
        lazy: () =>
          import("@/features/absences/AbsencesPage").then((m) => ({ Component: m.AbsencesPage })),
      },
      {
        path: routes.vacationNew,
        lazy: () =>
          import("@/features/absences/VacationNewPage").then((m) => ({
            Component: m.VacationNewPage,
          })),
      },
      {
        path: routes.azkNew,
        lazy: () =>
          import("@/features/absences/VacationNewPage").then((m) => ({
            Component: m.AzkNewPage,
          })),
      },
      {
        path: routes.sickNew,
        lazy: () =>
          import("@/features/absences/SickNewPage").then((m) => ({ Component: m.SickNewPage })),
      },
      {
        path: "/absences/:id",
        lazy: () =>
          import("@/features/absences/AbsenceDetailPage").then((m) => ({
            Component: m.AbsenceDetailPage,
          })),
      },
      {
        path: routes.home,
        lazy: () => import("@/features/week/WeekPage").then((m) => ({ Component: m.WeekPage })),
      },
      {
        path: "/entries/new",
        lazy: () => import("@/features/entry/EntryPage").then((m) => ({ Component: m.EntryPage })),
      },
      {
        path: "/entries/:id",
        lazy: () => import("@/features/entry/EntryPage").then((m) => ({ Component: m.EntryPage })),
      },
    ],
  },
  {
    element: <RequireRole roles={["employee", "dispatcher", "admin"]} />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: routes.profile,
        lazy: () =>
          import("@/features/profile/ProfilePage").then((m) => ({ Component: m.ProfilePage })),
      },
      {
        path: routes.notifications,
        lazy: () =>
          import("@/features/notifications/NotificationsPage").then((m) => ({
            Component: m.NotificationsPage,
          })),
      },
    ],
  },
  {
    element: <RequireRole roles={["dispatcher", "admin"]} />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: routes.dispo,
        lazy: () =>
          import("@/features/dispatch/inbox/InboxPage").then((m) => ({ Component: m.InboxPage })),
      },
      {
        path: routes.dispoAbsences,
        lazy: () =>
          import("@/features/dispatch/absences/DispatchAbsencesPage").then((m) => ({
            Component: m.DispatchAbsencesPage,
          })),
      },
      {
        path: "/dispo/times",
        lazy: () =>
          import("@/features/dispatch/times/DispatchTimesPage").then((m) => ({
            Component: m.DispatchTimesPage,
          })),
      },
      {
        path: routes.dispoEmployees,
        lazy: () =>
          import("@/features/employees/EmployeesPage").then((m) => ({
            Component: m.EmployeesPage,
          })),
      },
      {
        path: routes.dispoSync,
        lazy: () => import("@/features/sync/SyncPage").then((m) => ({ Component: m.SyncPage })),
      },
      {
        // Sync alerts link every staff user to /admin/sync; dispatchers read the same page.
        path: routes.adminSync,
        lazy: () => import("@/features/sync/SyncPage").then((m) => ({ Component: m.SyncPage })),
      },
    ],
  },
  {
    element: <RequireRole roles={["admin"]} />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: routes.adminUsers,
        lazy: () => import("@/features/admin/UsersPage").then((m) => ({ Component: m.UsersPage })),
      },
      {
        path: routes.adminSettings,
        lazy: () =>
          import("@/features/admin/SettingsPage").then((m) => ({ Component: m.SettingsPage })),
      },
    ],
  },
  { path: "*", element: <Navigate to={routes.home} replace /> },
]);
